import { TableSkeleton } from 'components/Skeleton';
import Table from 'components/Table';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildDisplayText, capitalizeFirstLetter } from 'utils';
import { buildTableRow } from 'views/Portfolio/helpers';
import { InitialiseTradeHistoryTable } from 'views/Trade/helpers';
import { useFetch } from 'views/Trade/hooks/useFetch';
import { fetchTransactionReport } from 'views/Trade/services/fetchTransactionReport';
import { DisplayTextKeys, QUERY_KEYS, TRANSACTION_TYPE_ENUM } from 'views/Trade/types';
import { AppContext } from 'context/ContextProvider';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import noResultImage from '../../../../assets/images/no_results_image.png';

const PAGE_SIZE = 120;
const TradeHistory = () => {
  const { gbpToTargetCurrency } = useContext(AppContext);
  const { t } = useTranslation();
  const location = useLocation();
  const initSubTab = location.state?.initSubTab ?? TRANSACTION_TYPE_ENUM.BUY;
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'trade:common', t), [t]);

  const [transactionType, setTransactionType] = useState<TRANSACTION_TYPE_ENUM>(initSubTab);

  const { isLoading: isLoadingTransactionReport, responce: transactionReportResponce } = useFetch({
    queryFn: () =>
      fetchTransactionReport({
        pageSize: PAGE_SIZE,
        transactionType: transactionType,
      }),
    queryKey: [`${QUERY_KEYS.FETCH_TRANSACTION_REPORT}${transactionType}${location.state?.initSubTab}`],
  });

  const [resultCount, setResultCount] = useState<number>(0);

  const { tradeHistoryColumns } = useMemo(
    () => InitialiseTradeHistoryTable(t, gbpToTargetCurrency),
    [t, gbpToTargetCurrency],
  );

  const tableBuilder = useMemo(() => {
    const source = transactionReportResponce?.data || [];

    source.sort((a, b) => moment(b.dateTime).diff(moment(a.dateTime)));
    setResultCount(source?.length || 0);
    const rows = (source || []).map((data) => {
      return {
        ...buildTableRow(
          {
            ...data,
          },
          tradeHistoryColumns,
        ),
      };
    });
    return { rows };
  }, [tradeHistoryColumns, transactionReportResponce]);

  return (
    <div className="flex flex-1 sm:p-5 bg-gray-50 dark:bg-slate-900 w-full flex-col">
      <div className="flex items-center gap-3 mb-4 justify-between px-4 py-2 md:px-0 md:py-0">
        <div className="flex items-center gap-3">
          <div className="flex gap-2 items-center">
            <div className="flex items-center text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
              {[TRANSACTION_TYPE_ENUM.BUY, TRANSACTION_TYPE_ENUM.SELL].map((tab, index) => (
                <button
                  key={tab}
                  onClick={() => setTransactionType(tab)}
                  className={`py-2 px-5 font-medium transition-all ${
                    index === 0 ? 'border-r border-gray-200 dark:border-slate-700' : ''
                  } ${
                    transactionType === tab
                      ? 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-inner'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  {capitalizeFirstLetter(t(`trade:common.${tab}`))}
                </button>
              ))}
            </div>
          </div>
          <div className="text-sm flex items-center gap-2 text-gray-600 dark:text-gray-400">
            {isLoadingTransactionReport ? (
              <div className="flex items-center gap-2">
                <p>{displayText[DisplayTextKeys.LOADING]}</p>
                <div className="w-4 h-4 border-2 border-gray-300 dark:border-slate-600 border-t-orange dark:border-t-teal-400 rounded-full animate-spin" />
              </div>
            ) : (
              <p>{`${displayText[DisplayTextKeys.RESULT]} ${resultCount}`}</p>
            )}
          </div>
        </div>
      </div>

      <div className="justify-center items-center bg-white dark:bg-slate-800 rounded-2xl flex-1 h-full shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700 overflow-hidden">
        {isLoadingTransactionReport && (
          <div className="p-4">
            <TableSkeleton rows={8} columns={5} />
          </div>
        )}

        {!isLoadingTransactionReport && tableBuilder.rows.length > 0 && (
          <Table
            columns={tradeHistoryColumns}
            rows={tableBuilder.rows}
            applyfadeOnScrollClassName={false}
            isFadeOnScrollDisable={true}
          />
        )}

        {!isLoadingTransactionReport && !tableBuilder.rows.length && (
          <div className="animate-[fade-in_5s_ease-out] p-8">
            <div className="bg-gray-50 dark:bg-slate-700/30 rounded-2xl p-8 text-center max-w-md mx-auto">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                <img className="w-12 h-12 object-contain" src={noResultImage} alt={t('common:noResult.title')} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('common:noResult.title')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('common:noResult.subTitle', 'No transaction history found')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradeHistory;
